import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  Input,
  ElementRef,
  AfterViewInit,
  Renderer2,
  ViewChild,
  ChangeDetectorRef,
  HostListener,
  Output,
  EventEmitter,
} from '@angular/core';
import { SettingsService } from 'services/settings.service';
import { sleep } from '@cf-shared/shared-util';
import { PageEnteredEvent } from 'app/app.component';
import { PubSubService } from 'services/pubsub.service';
import { MenuController } from 'providers/menu-controller';
import { LoggingService } from 'services/logging.service';
import { parseHintsSeen } from 'util/hints-seen';
import { describeError } from '@cf-shared/error-describe';
import { placeHintBubble } from 'util/hint-bubble-position';
import { resolveHintTarget } from './hint-target';

// whether a damaged hintsSeenJSON has been reported in this page load (see ngAfterViewInit)
let hintsSeenDamageReported = false;

@Component({
    selector: 'hint-bubble',
    templateUrl: 'hint-bubble.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HintBubbleComponent implements AfterViewInit {
  @Input() globalTarget: HTMLElement | string;
  @Input() target: HTMLElement | ElementRef<HTMLElement> | string;
  @Input() targetChild: string;
  // the element the inputs above resolved to, once ngAfterViewInit has run (see hint-target.ts)
  private resolvedTarget: HTMLElement | null = null;
  @Input() once: string;
  @Input() position: 'above' | 'below' = 'below';
  @Input() showMenu: boolean = false;
  @ViewChild('hintBubbleContent') hintBubbleContent: ElementRef;
  scrollElement: HTMLElement;

  @Output() shown = new EventEmitter<void>(); // triggers when the bubble appears

  @Input({ transform: booleanAttribute }) dismiss: boolean = false;
  @Output() dismissed = new EventEmitter<void>(); // triggers regardless of how the bubble disappears

  // if set, the bubble will only disappear when the target disappears - or if the bubble itself is ngIf'd away
  @Input() undismissable: boolean = false;

  @Input() acceptLabel: string;
  @Output() accepted = new EventEmitter<void>(); // only triggers when clicking on the actual bubble

  state: 'initial' | 'fadein' | 'fadeout' = 'initial';
  arrowX: number = 0;
  transitionDone = false; // just a delay to prevent user's initial click to already remove the bubble
  alreadyDestroyed = false; // used to prevent racing conditions when the bubble is dismissed (e.g. by page transition) before it finishes fading in
  myOldRect: DOMRect = undefined;
  targetObserver: MutationObserver = undefined;

  // event subscriber for Ionic needs a reference to the function to be able to deregister it :facepalm:
  pageEnteredSub: any;

  constructor(
    private renderer: Renderer2,
    private cd: ChangeDetectorRef,
    private settings: SettingsService,
    private menuCtrl: MenuController,
    private pubSub: PubSubService,
    private logging: LoggingService,
  ) { }

  async ngAfterViewInit() {

    await sleep(100);

    // if set up to show only once to the user
    if (this.once && !this.settings.debugShowAllHints) {
      // a damaged value shows the hint again, and the write below replaces it with a good one.
      // nothing in the app writes a damaged value, so damage is still worth seeing. once per page
      // load: a screen can hold several hint bubbles, and each reads this setting.
      const mySettings = parseHintsSeen(this.settings.hintsSeenJSON, err => {
        if (hintsSeenDamageReported) return;
        hintsSeenDamageReported = true;
        this.logging.trackSilentProblem('HintsSeenSettingDamaged', { detail: describeError(err) });
      });
      // if already seen
      if (mySettings[this.once]) {
        // just bail out of initialization
        return;
        // otherwise, we are seen _now_
      } else {
        // ... so store that info so we don't show again
        mySettings[this.once] = true;
        this.settings.hintsSeenJSON = JSON.stringify(mySettings);
        this.settings.saveInBackground('hint bubble seen');
      }
    }

    if (this.showMenu) {
      void this.menuCtrl.open();
    }


    this.resolvedTarget = resolveHintTarget(this.hintBubbleContent.nativeElement as HTMLElement, {
      globalTarget: this.globalTarget,
      target: this.target,
      targetChild: this.targetChild,
    });

    if (!this.resolvedTarget) {
      return;
    }

    this.scrollElement = this.resolvedTarget.closest('.scroll-content') as HTMLElement; // scroll element doesn't have to exist (if e.g. the target is in a header, modal, etc)

    // set the parent to somewhere high up the hierarchy, as ionic is forcing overflow:hidden on many lists, cards, etc
    // we are tried abusing toast-portal here, tried fixed-content, but something in ionic is emptying it after page load, etc
    // having the user specify the host near the top of hierarchy should do for now
    let hintHost = document.getElementsByClassName('hint-bubble-host')[0];
    if (hintHost) {
      hintHost.appendChild(this.hintBubbleContent.nativeElement);
    } else {
      console.error("Can't find .hint-bubble-host tag in the page - hints may be clipped wrong!");
    }

    let checkStillValid = () => {
      if (!this.alreadyDestroyed && this.resolvedTarget) {
        return true;
      } else {
        this.state = 'fadeout';
        return false;
      }
    };

    void (async () => {
      if (!checkStillValid()) {
        return;
      }
      // we wait for the view to init so we get correct coords after eventual fades
      await sleep(500);
      if (!checkStillValid()) {
        return;
      }
      this.reposition();

      // we dismiss on scroll - easier than trying to catch up scroll positions without stuttering
      if (this.scrollElement) {
        this.scrollElement.addEventListener('scroll', () => {
          this.reposition();
        });
      }

      await sleep(0);
      if (!checkStillValid()) {
        return;
      }
      this.state = 'fadein';
      await sleep(300);
      if (!checkStillValid()) {
        return;
      }
      this.transitionDone = true;
      this.shown.emit();

      this.installTimer();
      if (!this.pageEnteredSub) {
        this.pageEnteredSub = this.pubSub.subscribe(PageEnteredEvent, () => this.dismissBubble());
      }
    })();
  }

  // install a timer to repeatedly reposition the bubble every 100ms until it is dismissed
  // this in turn handles the case where some change in the page causes the target to become invisible - and it dismisses the bubble
  installTimer() {
    let timer = setInterval(() => {
      if (this.state !== 'fadein') {
        clearInterval(timer);
      } else {
        this.reposition();
      }
    }, 100);
  }

  // in case we get removed from the shadow DOM abruptly (e.g. *ngIf on parent evals to false suddenly)
  ngOnDestroy() {
    // this will handle deletion after transition is finished etc, but...
    this.dismissBubble();
    if (this.hintBubbleContent?.nativeElement) {
      // ...angular will no longer update us, so force the classes on the content this way
      (this.hintBubbleContent.nativeElement as HTMLElement).classList.remove('initial');
      (this.hintBubbleContent.nativeElement as HTMLElement).classList.remove('fadein');
      (this.hintBubbleContent.nativeElement as HTMLElement).classList.add('fadeout');
    }

    if (this.targetObserver) {
      this.targetObserver.disconnect();
    }
    if (this.pageEnteredSub) {
      this.pageEnteredSub.unsubscribe();
    }
  }

  isElementVisible(element: HTMLElement, root: HTMLElement) {
    // for each element in the hierarcy between this and root (or page, if root is not specifiex)
    for (let e = element; e && e != root && e.className !== 'cs-page'; e = e.parentElement) {
      let styles = window.getComputedStyle(e);
      // if any of them is hidden, return false

      if (styles.display === 'none' || styles.visibility === 'hidden' || styles.opacity === '0') {
        return false;
      }
    }
    return true;
  }


  reposition() {
    if (this.alreadyDestroyed) {
      return;
    }

    // with no target there is nothing to point at, so the bubble goes
    const target = this.resolvedTarget;
    if (!target) {
      this.dismissBubble();
      return;
    }

    // if the becomes target is hidden, the bubble is useless, so dismiss it
    // TODO - for undismissable bubbles, should perhaps catch the case where the target re-appears later and reshow the bubble?
    if (!this.isElementVisible(target, this.scrollElement)) {
      this.dismissBubble();
      return;
    }

    if (!this.myOldRect) {
      this.myOldRect = this.hintBubbleContent.nativeElement.getBoundingClientRect();
    }

    const placement = placeHintBubble(target.getBoundingClientRect(), this.myOldRect, window.innerWidth, this.position);
    this.arrowX = placement.arrowX;
    this.renderer.setStyle(this.hintBubbleContent.nativeElement, 'left', `${placement.leftDelta}px`);
    this.renderer.setStyle(this.hintBubbleContent.nativeElement, 'top', `${placement.topDelta}px`);

    // note: This is needed to prevent ExpressionChangedAfterItHasBeenCheckedError caused by setting the arrowX propery above.
    // That happens because this.arrowX is bound to style.left.px on the arrow.
    // Alternatively we could try to look up arrow by id and update it through the this.renderer, but this works for now.
    this.cd.markForCheck();
  }

  dismissBubble() {
    this.dismissed.emit();
    this.alreadyDestroyed = true;
    this.state = 'fadeout';
    if (this.hintBubbleContent?.nativeElement) {
      (this.hintBubbleContent.nativeElement as HTMLElement).addEventListener('transitionend', () => {
        (this.hintBubbleContent.nativeElement as HTMLElement).remove();
      });
    }
  }

  @HostListener('window:mousedown', ['$event'])
  onClickAnywhere(e: Event) {
    if (this.alreadyDestroyed || !this.transitionDone) {
      return;
    }
    if (this.undismissable) {
      this.reposition();
      return;
    }
    // hints with accept label have to let clicks through to that target
    if (this.acceptLabel && (e.target as HTMLElement).closest('.dismiss-link')) {
    // this is a click on the accept button, so let it through
      return;
    }

    if (this.state === 'fadein' && this.dismiss && this.transitionDone && e.srcElement != this.hintBubbleContent.nativeElement) {
      e.stopPropagation(); // prevents bubbling when control is part of a bigger clickable control
      this.dismissBubble();
    } else {
      this.dismissBubble();
    }
  }
  @HostListener('window:resize', ['$event'])
  onWindowResize(e: Event) {
    void (e);
    if (this.state === 'initial') {
      return;
    }
    this.reposition();
  }


  onClickAccept(event) {
    event.stopPropagation(); // prevents bubbling when control is part of a bigger clickable control
    this.dismissBubble();
    this.accepted.emit();
  }
}
